import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentGetStartedData,
  type EnrollmentGetStartedSubmitInput,
  type EnrollmentGetStartedSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment GetStarted data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentGetStartedData>
 * @example const response = await getEnrollmentGetStartedData()
 */
export const getEnrollmentGetStartedData = async (context?: DefaultContext): Promise<EnrollmentGetStartedData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentGetStartedData',
      query: `mutation getEnrollmentGetStartedData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentGetStartedData }
  return response.submitEnrollment
}

export const useGetEnrollmentGetStartedData = createQueryHook<undefined, EnrollmentGetStartedData>(
  getEnrollmentGetStartedData,
)

/**
 * Submit Enrollment GetStarted data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentGetStartedSubmitOutput>
 * @example const response = await submitEnrollmentGetStartedData();
 */
export const submitEnrollmentGetStartedData = async (
  enrollmentGetStartedSubmitInput: EnrollmentGetStartedSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentGetStartedSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentGetStartedData',
      query: `mutation submitEnrollmentGetStartedData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentGetStartedSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentGetStartedSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentGetStartedData = createMutationHook<
  EnrollmentGetStartedSubmitInput,
  EnrollmentGetStartedSubmitOutput
>(submitEnrollmentGetStartedData)
